import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import { parseApiJson } from "../utils/apiHelpers";

interface User {
  username: string;
  role: "admin" | "user";
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  token: string | null;
  login: (
    username: string,
    password: string,
  ) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

interface LoginApiResponse {
  success: boolean;
  token?: string;
  user?: {
    username: string;
    role: "admin" | "user";
  };
  retry_after?: number;
  message?: string;
}

// API base URL - '/api' is proxied to PHP server (localhost:8000 in dev)
const API_URL = process.env.REACT_APP_API_URL || "/api";

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);

  // Check for stored auth on mount
  useEffect(() => {
    const storedUser = localStorage.getItem("travel_engine_user");
    const storedToken = localStorage.getItem("travel_engine_token");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    if (storedToken) {
      setToken(storedToken);
    }
    setIsLoading(false);
  }, []);

  const login = async (
    username: string,
    password: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      // Call PHP backend API for authentication
      const endpoint = `${API_URL}/login.php`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await parseApiJson<LoginApiResponse>(response);

      if (response.ok && data.success && data.user && data.token) {
        const userData: User = {
          username: data.user.username,
          role: data.user.role,
        };

        setUser(userData);
        setToken(data.token);

        localStorage.removeItem("travel_engine_token");
        // Store token and user data
        localStorage.setItem("travel_engine_token", data.token);
        localStorage.setItem("travel_engine_user", JSON.stringify(userData));

        return { success: true };
      }

      // Handle different error status codes
      if (response.status === 429) {
        const retryAfter = data.retry_after
          ? Math.ceil(data.retry_after / 60)
          : 15;
        return {
          success: false,
          error: `Troppi tentativi di accesso. Riprova tra ${retryAfter} minuti.`,
        };
      }

      if (response.status === 401) {
        return {
          success: false,
          error: "Nome utente o password non validi",
        };
      }

      // Generic error message from server
      return {
        success: false,
        error: data.message || "Errore durante l'accesso. Riprova.",
      };
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        error: "Errore di connessione. Verifica la tua connessione internet.",
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("travel_engine_user");
    localStorage.removeItem("travel_engine_token");
    // Force a slight delay to allow navigation to complete
    setTimeout(() => {
      window.location.href = "/";
    }, 0);
  };

  const isAuthenticated = user !== null;
  const isAdmin = user?.role === "admin";

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isAdmin,
        isLoading,
        token,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
